import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Clock } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { SEO } from "@/components/common/SEO";
import { formatDate } from "@/lib/utils-date";
import api from "@/lib/api";

const REVEAL_DELAY = 500;

export default function Events() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [animateReady, setAnimateReady] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => {
      setUpcoming(r.data.filter((e) => !e.is_past));
      setPast(r.data.filter((e) => e.is_past));
      setLoaded(true);
    });
  }, []);

  // White background delay — give content time to render, then reveal
  useEffect(() => {
    if (!loaded) return;
    const timer = setTimeout(() => setAnimateReady(true), REVEAL_DELAY);
    return () => clearTimeout(timer);
  }, [loaded]);

  const featured = upcoming[0];
  const rest = upcoming.slice(1);

  return (
    <div data-testid="events-page" className="pt-20">
      <SEO
        title="Exhibitions & Events | Rain Boy Fine Art Tulsa"
        description="Discover upcoming exhibitions, openings, artist talks, and gallery events at Rain Boy Fine Art in Tulsa, OK. Join us for the next opening reception."
        path="/events"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Events", path: "/events" }]}
        schemas={[{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Exhibitions & Events | Rain Boy Fine Art", url: "https://rainboyfineart.com/events", description: "Upcoming exhibitions, openings, and gallery events at Rain Boy Fine Art in Tulsa, OK." }]}
      />
      {/* Header — static content, FadeIn works fine */}
      <section className="py-16 md:py-24 px-6 lg:px-12 pb-0 md:pb-0">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <p className="overline mb-4">What's Happening</p>
            <h1 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] mb-6">
              Upcoming Events
            </h1>
            <p className="text-[#4A5568] max-w-2xl mb-0 text-base leading-relaxed">
              From exhibition openings and artist talks to workshops and community gatherings,
              there's always something happening at Rain Boy Fine Art.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Reserve space while API is fetching */}
      {!loaded && <div className="min-h-[60vh]" />}

      {/* Featured Event */}
      {loaded && featured && (
        <section className="py-10 md:py-14 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <FadeIn ready={animateReady}>
              <Link
                to={`/events/${featured.slug || featured.id}`}
                className="group block"
                data-testid="upcoming-event-0"
              >
                <div className="flex flex-col lg:flex-row overflow-hidden relative">
                  {featured.image && (
                    <div className="aspect-[4/3] lg:aspect-auto lg:absolute lg:inset-y-0 lg:left-0 lg:w-1/2 overflow-hidden">
                      <img
                        src={featured.image}
                        alt={featured.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="bg-[#0A1128] p-8 md:p-10 lg:p-12 flex flex-col justify-center lg:w-1/2 lg:ml-auto">
                    <p className="overline mb-4">Featured Event</p>

                    <div className="flex items-start gap-6 mb-6">
                      {/* Date callout with corner brackets */}
                      <div className="relative flex-shrink-0 w-20 h-24 flex flex-col items-center justify-center">
                        <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#CBA365]" />
                        <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#CBA365]" />
                        <span className="text-4xl font-heading text-white leading-none">{new Date(featured.date + 'T00:00:00').getDate()}</span>
                        <span className="text-xs tracking-[0.2em] text-[#CBA365] mt-1">{new Date(featured.date + 'T00:00:00').toLocaleString('en-US', { month: 'short' }).toUpperCase()}</span>
                        <span className="text-[10px] text-white/50">{new Date(featured.date + 'T00:00:00').getFullYear()}</span>
                      </div>

                      <div>
                        <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3 leading-tight group-hover:text-white/80 transition-colors">
                          {featured.title}
                        </h2>
                        <p className="text-sm text-white/70 leading-relaxed">{featured.description}</p>
                      </div>
                    </div>

                    {/* Time & Location row */}
                    <div className="flex flex-wrap gap-6 mb-8 text-sm">
                      <span className="flex items-center gap-2 text-[#CBA365]">
                        <Clock size={13} />
                        <span className="text-white/60">{featured.time}{featured.end_time ? ` - ${featured.end_time}` : ""}</span>
                      </span>
                      {featured.location && (
                        <span className="flex items-center gap-2 text-[#CBA365]">
                          <MapPin size={13} />
                          <span className="text-white/60">Rain Boy Fine Art, Tulsa</span>
                        </span>
                      )}
                    </div>

                    <span className="btn-fill-hover inline-flex items-center gap-3 border border-[#CBA365] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] self-start" style={{'--fill-color': '#CBA365'}}>
                      Event Details <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Remaining Upcoming Events */}
      {loaded && rest.length > 0 && (
        <section className="pb-16 md:pb-24 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((event, i) => (
                <FadeIn key={event.id} ready={animateReady}>
                  <Link
                    to={`/events/${event.slug || event.id}`}
                    className="group block h-full"
                    data-testid={`upcoming-event-${i + 1}`}
                  >
                    <div className="h-full border border-[#0A1128]/10 hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                      {event.image && (
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-start gap-4 mb-3">
                          <div className="relative flex-shrink-0 w-14 h-16 flex flex-col items-center justify-center">
                            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#CBA365]" />
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#CBA365]" />
                            <span className="text-2xl font-heading text-[#0A1128] leading-none">{new Date(event.date + 'T00:00:00').getDate()}</span>
                            <span className="text-[9px] tracking-[0.2em] text-[#CBA365] mt-0.5">{new Date(event.date + 'T00:00:00').toLocaleString('en-US', { month: 'short' }).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="flex flex-wrap gap-3 mb-2 text-xs text-[#8A94A6]">
                              <span className="flex items-center gap-1.5"><Clock size={11} className="text-[#CBA365]" /> {event.time}</span>
                            </div>
                            <h3 className="font-heading text-lg text-[#0A1128] mb-2 group-hover:text-[#2c3e50] transition-colors leading-snug">
                              {event.title}
                            </h3>
                          </div>
                        </div>
                        <p className="text-sm text-[#4A5568] line-clamp-2 mb-4">{event.description}</p>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#8A94A6] group-hover:text-[#0A1128] transition-colors flex items-center gap-2">
                          Details <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {loaded && upcoming.length === 0 && (
        <section className="py-16 px-6">
          <p className="text-[#8A94A6] text-center">No upcoming events at this time. Check back soon!</p>
        </section>
      )}

      {/* Past Events */}
      {loaded && past.length > 0 && (
        <section className="bg-[#F8F9FA] py-16 md:py-24 px-6 lg:px-12" data-testid="past-events-section">
          <div className="max-w-6xl mx-auto">
            <FadeIn ready={animateReady}>
              <p className="overline mb-4">Archive</p>
              <h2 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] mb-12">
                Past Events
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {past.map((event, i) => (
                <FadeIn key={event.id} ready={animateReady}>
                  <Link
                    to={`/events/${event.slug || event.id}`}
                    className="group block"
                    data-testid={`past-event-${i}`}
                  >
                    {event.image && (
                      <div className="aspect-[16/10] overflow-hidden mb-4 opacity-70 group-hover:opacity-100 transition-opacity duration-500">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                        />
                      </div>
                    )}
                    <h3 className="font-heading text-base text-[#0A1128] mb-1 group-hover:text-[#2c3e50] transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-xs text-[#8A94A6]">{formatDate(event.date)}</p>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
